import { useState, useEffect, useRef } from "react"
import { X, ArrowLeft } from "lucide-react"
import { getUserScheduleService } from "../../utils/userService"

const DAYS_OF_WEEK = [
  { short: "S", full: "Sunday" },
  { short: "M", full: "Monday" },
  { short: "T", full: "Tuesday" },
  { short: "W", full: "Wednesday" },
  { short: "T", full: "Thursday" },
  { short: "F", full: "Friday" },
  { short: "S", full: "Saturday" },
];

const DEFAULT_TIME_OPTIONS = [
  { hour: "", min: "" },
  { hour: "", min: "" },
  { hour: "", min: "" },
];

const EmailProcessingSchedule = ({ isOpen, onClose, onBack, onSave }) => {
  const [selectedDays, setSelectedDays] = useState([]);
  // Instead of storing just ["HH:MM", ...], store [{hour: "HH", min: "MM"}, ...]
  const [selectedTimes, setSelectedTimes] = useState([
    { hour: "", min: "" },
    { hour: "", min: "" },
    { hour: "", min: "" }
  ]);
  const getUserScheduleCalled = useRef(false);

  // Hours selector (from 4:00 to 23:00 and 0:00 to 3:00)
  const hourOptions = [];
  for (let hour = 4; hour <= 23; hour++) {
    const time12 = hour === 0 ? "12:00 AM" :
                  hour === 12 ? "12:00 PM" :
                  hour > 12 ? `${hour - 12}:00 PM` : `${hour}:00 AM`;
    hourOptions.push({
      value: hour.toString().padStart(2, '0'),
      label: time12.replace(":00", ""),
    });
  }
  for (let hour = 0; hour <= 3; hour++) {
    const time12 = hour === 0 ? "12:00 AM" : `${hour}:00 AM`;
    hourOptions.push({
      value: hour.toString().padStart(2, '0'),
      label: time12.replace(":00", ""),
    });
  }

  // 15 Minutes interval selector
  const minuteOptions = [
    { value: "00", label: ":00" },
    { value: "15", label: ":15" },
    { value: "30", label: ":30" },
    { value: "45", label: ":45" },
  ];

  useEffect(() => {
    const fetchUserSchedule = async () => {
      if (getUserScheduleCalled.current) return;
      getUserScheduleCalled.current = true;

      try {
        const userSchedule = await getUserScheduleService();

        if (userSchedule && Array.isArray(userSchedule)) {
          // Convert day names to indices and collect times
          const days = new Set();
          const times = [];

          userSchedule.forEach(schedule => {
            // Convert day name to index (0-6)
            const dayIndex = DAYS_OF_WEEK.findIndex(day => day.full === schedule.day_of_week);
            if (dayIndex !== -1) {
              days.add(dayIndex);
            }

            // Convert time format from "HH:MM:SS" to "HH" and "MM"
            const [hour, min] = schedule.time_of_day.split(':');
            times.push({
              hour: hour.padStart(2, "0"),
              min: min.padStart(2, "0"),
            });
          });

          setSelectedDays(Array.from(days));

          // Ensure we have exactly 3 time slots
          const paddedTimes = [...times];
          while (paddedTimes.length < 3) {
            paddedTimes.push({ hour: "", min: "" });
          }
          setSelectedTimes(paddedTimes.slice(0, 3));
        }
      } catch (error) {
        console.error("Error fetching user schedule:", error);
        // Keep default values if there's an error
        setSelectedDays([]);
        setSelectedTimes(DEFAULT_TIME_OPTIONS);
      }
      finally {
        getUserScheduleCalled.current = false;
      }
    };
    fetchUserSchedule();
  }, []);

  const handleDayToggle = (dayIndex) => {
    setSelectedDays((prev) =>
      prev.includes(dayIndex) ? prev.filter((d) => d !== dayIndex) : [...prev, dayIndex]
    );
  };

  // timePart is either "hour" or "min"
  const handleTimeChange = (timeIndex, timePart, value) => {
    setSelectedTimes((prev) => {
      const newTimes = [...prev];
      newTimes[timeIndex] = {
        ...newTimes[timeIndex],
        [timePart]: value
      };
      return newTimes;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Convert selected days and times back to the API format
    const validTimeSlots = selectedTimes.filter(time => time.hour && (time.min !== null && time.min !== undefined));
    const scheduleData = selectedDays.flatMap(dayIndex =>
      validTimeSlots.map(time => ({
        day_of_week: DAYS_OF_WEEK[dayIndex].full,
        time_of_day: `${time.hour}:${time.min || "00"}:00` // Add seconds to match API format
      }))
    );
    onSave(scheduleData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-[1000]">
      <div className="bg-white p-8 rounded-lg w-[90%] max-w-[600px] shadow-md">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              data-amp-track-section="Email Processing Schedule"
              data-amp-track-action="Back_Click"
              data-amp-track-description="Back to Email Preferences"
              className="bg-transparent border-none hover:bg-gray-100 rounded-lg p-2 cursor-pointer text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex flex-col gap-2">
              <h2 className="m-0 text-gray-800">Email Processing Schedule</h2>
              <p className="text-gray-600 text-sm">Configure <i>when</i> Geoffrey processes your email. Geoffrey will process your email at the times you select.</p>
            </div>
          </div>
          <button
            className="bg-transparent border-none hover:bg-gray-100 rounded-lg p-2 text-2xl cursor-pointer text-gray-600 hover:text-gray-800"
            onClick={onClose}
            data-amp-track-section="Email Processing Schedule"
            data-amp-track-action="Close_Click"
            data-amp-track-description="Close Email Processing Schedule"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          {/* Days of Week Selection */}
          <div>
            <h3 className="text-base font-semibold text-gray-800 mb-4">Process on</h3>
            <div className="flex gap-1">
              {DAYS_OF_WEEK.map((day, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleDayToggle(index)}
                  data-amp-track-section="Email Processing Schedule"
                  data-amp-track-action="Day_Toggle_Click"
                  data-amp-track-description={`Toggle ${day.full}`}
                  className={`w-10 h-10 flex items-center justify-center rounded-full border border-solid font-medium transition-colors duration-200 ${
                    selectedDays?.includes(index)
                      ? "bg-sky-500 border-sky-500 text-white hover:bg-sky-600"
                      : "bg-white border-gray-300 text-gray-700 hover:border-gray-300 hover:bg-gray-100"
                  }`}
                  title={day.full}
                >
                  {day.short}
                </button>
              ))}
            </div>
          </div>

          {/* Time of Day Selection */}
          <div>
            <h3 className="text-base font-semibold text-gray-800 mb-4">Time of day</h3>
            <div className="flex flex-col gap-4">
              {(selectedTimes || DEFAULT_TIME_OPTIONS).map((time, index) => (
                <div key={index} className="flex items-center gap-3">
                  <label className="text-sm text-gray-600 w-16">Time {index + 1}:</label>
                  {/* Hour selector */}
                  <select
                    value={time.hour || ""}
                    onChange={(e) => handleTimeChange(index, "hour", e.target.value)}
                    data-amp-track-section="Email Processing Schedule"
                    data-amp-track-action="Time_Hour_Change"
                    data-amp-track-description={`Time ${index + 1} Hour Change`}
                    className="py-2 px-3 border border-gray-300 rounded-md text-base focus:outline-none focus:border-sky-500 min-w-[85px]"
                  >
                    <option value="">Hour</option>
                    {hourOptions?.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {/* Minute selector */}
                  <select
                    value={time.min || ""}
                    onChange={(e) => handleTimeChange(index, "min", e.target.value)}
                    data-amp-track-section="Email Processing Schedule"
                    data-amp-track-action="Time_Min_Change"
                    data-amp-track-description={`Time ${index + 1} Minute Change`}
                    className="py-2 px-3 border border-gray-300 rounded-md text-base focus:outline-none focus:border-sky-500 min-w-[65px]"
                  >
                    <option value="">Minute</option>
                    {minuteOptions?.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-8">
            <button
              type="button"
              className="py-3 px-6 rounded-md text-base cursor-pointer transition-colors duration-200 bg-gray-100 border border-gray-300 text-gray-800 hover:bg-gray-200"
              onClick={onBack}
              data-amp-track-section="Email Processing Schedule"
              data-amp-track-action="Back_Click"
              data-amp-track-description="Back to Email Preferences"
            >
              Back
            </button>
            <button
              type="submit"
              className="py-3 px-6 rounded-md text-base cursor-pointer transition-colors duration-200 bg-sky-500 border-none text-white hover:bg-sky-600"
              data-amp-track-section="Email Processing Schedule"
              data-amp-track-action="Save_Click"
              data-amp-track-description="Save Email Processing Schedule"
            >
              Save Schedule
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailProcessingSchedule;
